import React from 'react';
import type { FC, ReactNode } from 'react';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		width: '100%',
		paddingBlock: token('space.150'),
		paddingInline: token('space.150'),
		backgroundColor: 'transparent',
		textAlign: 'left',
		// Picks up whichever corner radius `PressableGroup` gave this row's wrapper, so a hover
		// background follows the group's outer corners.
		borderRadius: 'inherit',
	},
	interactive: {
		transition: token('motion.listitem.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: '0px',
		flex: 1,
		gap: token('space.0'),
	},
	name: {
		font: token('font.body.small'),
		fontWeight: token('font.weight.semibold'),
		color: token('color.text'),
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
	role: {
		font: token('font.body.small'),
		color: token('color.text.subtle'),
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		textOverflow: 'ellipsis',
	},
});

export type PersonRowProps = {
	/** A node so the caller picks the avatar treatment, whether that is a photo or initials. */
	avatar: ReactNode;
	name: string;
	/**
	 * Secondary line under the name, such as a job title. Optional because a `CustomerUser` has
	 * no extended profile, so those rows collapse to the name rather than leave a blank line.
	 */
	role?: string;
	/** Omit when the row has no available action, such as an agent without a profile destination. */
	onClick?: () => void;
	testId?: string;
};

/**
 * A person or team as a row inside a `PressableGroup`: avatar, name, and a secondary line.
 *
 * Unlike `GoalOrProjectRow` the avatar is a left column rather than inline with the name, matching
 * how People & Agents rows are laid out in the Teamwork Graph panel.
 */
export const PersonRow: FC<PersonRowProps> = ({ avatar, name, role, onClick, testId }) => {
	const content = (
		<>
			{avatar}
			<Box xcss={styles.content}>
				<Box xcss={styles.name}>{name}</Box>
				{role ? <Box xcss={styles.role}>{role}</Box> : null}
			</Box>
		</>
	);
	return onClick ? (
		<Pressable xcss={cx(styles.row, styles.interactive)} onClick={onClick} testId={testId}>
			{content}
		</Pressable>
	) : (
		<Box xcss={styles.row} testId={testId}>
			{content}
		</Box>
	);
};
